import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../api/axiosClient";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/Form.css";
import TextField from "@mui/material/TextField";
import SidebarGuideline from "./SidebarGuideline";
import {downloadFile} from '../common'

// Constants for max lengths
const FIELD_MAX_LENGTH = 100;
const COMPANY_PROFILE_MAX_LENGTH = 300;
const COMMENT_MAX_LENGTH = 200;
const TEXTAREA_MAX_LENGTH = 300;
const PHONE_MAX_LENGTH = 10;

// Validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{10}$/;

const RegistrationGNZ = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeItem, setActiveItem] = useState(null);

  const [userInfo, setUserInfo] = useState();
  const [formId, setFormId] = useState("");
  const [PkId, setPkId] = useState(0);

  const [formData, setFormData] = useState({
    Organisationname: "",
    category: "Goal Net Zero Company of the Year",
    companyName: "",
    mailingAddress: "",
    authorityName: "",
    authorityTitle: "",
    authorityPhone: "",
    authorityLandline: "",
    authorityEmail: "",
    applicant_name: "",
    applicant_phone: "",
    applicant_email: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    companyProfile: "",
    approvingAuthoritySignature: "",
    declaration: false,
    comment: "",
    // Variables
    netZeroTarget2024: "",
    netZeroTarget2023: "",
    carbonEmitted2024: "",
    carbonEmitted2023: "",
    energyConsumed2024: "",
    energyConsumed2023: "",
    annualRevenue2024: "",
    annualRevenue2023: "",
    capexWind2024: "",
    capexWind2023: "",
    capexSolar2024: "",
    capexSolar2023: "",
    capexOtherRE2024: "",
    capexOtherRE2023: "",
    rePowerProd2024: "",
    rePowerProd2023: "",
    powerConsumed2024: "",
    powerConsumed2023: "",
    gh2Investment2024: "",
    gh2Investment2023: "",
    gh2Production2024: "",
    gh2Production2023: "",
    treePlantation2024: "",
    treePlantation2023: "",
    carbonCapture2024: "",
    carbonCapture2023: "",
    ccsCapex2024: "",
    ccsCapex2023: "",
    carbonCaptured2024: "",
    carbonCaptured2023: "",
    // Attachments
    attachments1: { description: "", file: null },
    attachments2: { description: "", file: null },
    attachments3: { description: "", file: null },
    attachments4: { description: "", file: null },
    // Individual technology variables (5 rows)
    activityName1: "",
    plannedActivities1: "",
    actualProgress1: "",
    activityName2: "",
    plannedActivities2: "",
    actualProgress2: "",
    activityName3: "",
    plannedActivities3: "",
    actualProgress3: "",
    activityName4: "",
    plannedActivities4: "",
    actualProgress4: "",
    activityName5: "",
    plannedActivities5: "",
    actualProgress5: "",
  });

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("user_info");
      if (stored) {
        const user = JSON.parse(stored);
        setUserInfo(JSON.parse(stored));
        setFormId(`GNZRegistration_${user.id}`);

        setFormData((prev) => ({
          ...prev,
          userid: user.id || "",
          firstname: user.first_name || "",
          lastname: user.last_name || "",
          company_name: user.organisation_name || "",
          Organisationname: user.organisation_name || "",
          applicant_name: user.applicant_name || "",
          applicant_phone: user.applicant_phone || "",
          applicant_email: user.email || "",
        }));
      }
    } catch (err) {
      console.warn("Failed to parse user_info from localStorage:", err);
    }
  }, []);

  // This use Effect Prefill user Data using api;
  useEffect(() => {
    if (userInfo && formId) {
      prefillFormData();
    } else {
      console.warn(
        `userInfo OR formId is invalid; Kindly check the method. and data`
      );
    }
  }, [userInfo, formId]);

  const isSubmited = () => formData.form_mode === "submit";

  // Helper: safe stringify / empty -> string
  function safeStr(v) {
    if (v === null || typeof v === "undefined") return "";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  }

  // Helper: parse/format server date -> YYYY-MM-DD, but keep empty when invalid
  function formatDateForInput(val) {
    if (!val && val !== 0) return "";
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    const d = val instanceof Date ? val : new Date(val);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
    return "";
  }

  async function prefillFormData() {
    if (!formId) return;

    // Build URL: include user_id if we have it (many endpoints accept it)
    const url =
      typeof userInfo !== "undefined" && userInfo && userInfo.id
        ? `/goal-net-zero/?user_id=${userInfo.id}&form_id=${formId}`
        : `/goal-net-zero/?form_id=${formId}`;

    try {
      const res = await apiClient.get(url);

      // Expect array response; accept single-item or first item
      if (!Array.isArray(res.data) || res.data.length < 1) {
        console.log("No prefill data found:", res.data);
        return null;
      }

      const item = res.data[0];
      console.log("prefill: server item:", item);
      setPkId(item.id ?? item.pk ?? 0);

      // qMap: front-end base => backend base name
      const qMap = {
        netZeroTarget: "net_zero_target",
        carbonEmitted: "carbon_emitted",
        energyConsumed: "energy_consumed",
        annualRevenue: "annual_revenue",
        capexWind: "capex_wind",
        capexSolar: "capex_solar",
        capexOtherRE: "capex_other_re",
        rePowerProd: "re_power_prod",
        powerConsumed: "power_consumed",
        gh2Investment: "gh2_investment",
        gh2Production: "gh2_production",
        treePlantation: "tree_plantation",
        carbonCapture: "carbon_capture",
        ccsCapex: "ccs_capex",
        carbonCaptured: "carbon_captured",
      };

      // Start building newFields mapping all frontend keys
      const newFields = {
        // user/meta
        userid: item.userid ?? item.user_id ?? (userInfo && userInfo.id) ?? "",
        firstname:
          item.firstname ??
          item.first_name ??
          (userInfo && userInfo.first_name) ??
          "",
        lastname:
          item.lastname ??
          item.last_name ??
          (userInfo && userInfo.last_name) ??
          "",
        company_name:
          item.company_name ?? item.companyName ?? item.organisation_name ?? "",

        // identity / org
        organisation_name:
          item.organisation_name ??
          item.organisationName ??
          item.company_name ??
          item.companyName ??
          "",
        Organisationname:
          item.Organisationname ??
          item.organisation_name ??
          item.organisationName ??
          item.company_name ??
          "",
        companyName: item.company_name ?? item.companyName ?? "",

        category: item.category ?? "",
        mailingAddress: item.mailing_address ?? item.mailingAddress ?? "",

        // authority
        authorityName: item.authority_name ?? item.authorityName ?? "",
        authorityTitle: item.authority_title ?? item.authorityTitle ?? "",
        authorityPhone: item.authority_phone ?? item.authorityPhone ?? "",
        authorityLandline:
          item.authority_landline ?? item.authorityLandline ?? "",
        authorityEmail: item.authority_email ?? item.authorityEmail ?? "",

        // contacts / applicant
        applicant_name:
          item.applicant_name ?? item.applicantName ?? item.contact_name ?? "",
        applicant_phone:
          item.applicant_phone ??
          item.applicantPhone ??
          item.contact_phone ??
          "",
        applicant_email:
          item.applicant_email ??
          item.applicantEmail ??
          item.contact_email ??
          "",
        contact_name: item.contact_name ?? item.contactName ?? "",
        contact_phone: item.contact_phone ?? item.contactPhone ?? "",
        contact_email: item.contact_email ?? item.contactEmail ?? "",

        // narrative / meta
        companyProfile: item.company_profile ?? item.companyProfile ?? "",
        awardJustification:
          item.award_justification ?? item.awardJustification ?? "",
        comment: item.comment ?? "",
        declaration:
          item.declaration === true ||
          item.declaration === "true" ||
          item.declaration === 1 ||
          item.declaration === "1"
            ? true
            : false,
        form_mode: item.form_mode ?? item.formMode ?? "draft",
        form_id: item.form_id ?? item.formId ?? formId,

        // quantitative placeholders (will populate below)
        // attachments placeholders
        attachments1: { description: "", file: null, url: null },
        attachments2: { description: "", file: null, url: null },
        attachments3: { description: "", file: null, url: null },
        attachments4: { description: "", file: null, url: null },

        // approving authority signature placeholders
        approvingAuthoritySignature: null,
        approvingAuthoritySignatureUrl: null,

        // activity rows (flat) default
        activityName1: "",
        plannedActivities1: "",
        actualProgress1: "",
        activityName2: "",
        plannedActivities2: "",
        actualProgress2: "",
        activityName3: "",
        plannedActivities3: "",
        actualProgress3: "",
        activityName4: "",
        plannedActivities4: "",
        actualProgress4: "",
        activityName5: "",
        plannedActivities5: "",
        actualProgress5: "",
      };

      // Populate quantitative front/back 2024 & 2023 using qMap
      Object.entries(qMap).forEach(([front, base]) => {
        const v2024 =
          item[`${base}_2024`] ??
          item[`${base}_2024`.toLowerCase()] ??
          item[`${front}2024`] ??
          item[`${front}_2024`] ??
          null;
        const v2023 =
          item[`${base}_2023`] ??
          item[`${base}_2023`.toLowerCase()] ??
          item[`${front}2023`] ??
          item[`${front}_2023`] ??
          null;

        newFields[`${front}2024`] = safeStr(v2024);
        newFields[`${front}2023`] = safeStr(v2023);
      });

      // Map simple direct numeric/text fields if backend used explicit keys
      // e.g., if earlier code also uses netZeroTarget2024 already set above.

      // Map flat activity rows (parent fields) - attempt multiple key name patterns
      for (let i = 1; i <= 5; i++) {
        newFields[`activityName${i}`] =
          item[`activityName${i}`] ??
          item[`activity_name_${i}`] ??
          item[`activity_name${i}`] ??
          item[`activity${i}_name`] ??
          item.technologies?.[i - 1]?.activity_name ??
          "";
        newFields[`plannedActivities${i}`] =
          item[`plannedActivities${i}`] ??
          item[`planned_activities_${i}`] ??
          item[`plannedActivities${i}`] ??
          item.technologies?.[i - 1]?.planned_activities ??
          "";
        newFields[`actualProgress${i}`] =
          item[`actualProgress${i}`] ??
          item[`actual_progress_${i}`] ??
          item[`actualProgress${i}`] ??
          item.technologies?.[i - 1]?.actual_progress ??
          "";
      }

      // Child technologies: server might store as JSON string, related set, or array
      let childTechnologies = [];
      if (Array.isArray(item.technologies)) {
        childTechnologies = item.technologies;
      } else if (typeof item.technologies === "string" && item.technologies) {
        try {
          childTechnologies = JSON.parse(item.technologies);
        } catch (e) {
          console.warn("Could not parse technologies JSON:", e);
        }
      } else if (Array.isArray(item.gnztechnology_set)) {
        childTechnologies = item.gnztechnology_set;
      } else if (Array.isArray(item.technology_set)) {
        childTechnologies = item.technology_set;
      } else if (
        item.child_technologies &&
        Array.isArray(item.child_technologies)
      ) {
        childTechnologies = item.child_technologies;
      }

      // If childTechnologies provided, map first five into activity rows (prefer child data)
      if (childTechnologies.length > 0) {
        for (let i = 1; i <= 5; i++) {
          const row = childTechnologies[i - 1] || {};
          if (row) {
            newFields[`activityName${i}`] =
              row.activity_name ?? row.name ?? newFields[`activityName${i}`];
            newFields[`plannedActivities${i}`] =
              row.planned_activities ??
              row.plan ??
              newFields[`plannedActivities${i}`];
            newFields[`actualProgress${i}`] =
              row.actual_progress ??
              row.progress ??
              newFields[`actualProgress${i}`];
          }
        }
      }

      // Attachments 1..4 (desc + url) - many fallback keys
      for (let i = 1; i <= 4; i++) {
        const descKeys = [
          `attachments${i}_desc`,
          `attachments${i}Desc`,
          `attachment${i}_desc`,
          `attachment${i}Desc`,
          `attachments_desc_${i}`,
        ];
        const fileKeys = [
          `attachments${i}`,
          `attachments${i}_url`,
          `attachment${i}`,
          `attachment${i}_url`,
          `attachments${i}_file`,
        ];

        let desc = "";
        for (const k of descKeys) {
          if (Object.prototype.hasOwnProperty.call(item, k)) {
            desc = item[k] ?? "";
            break;
          }
        }

        let url = null;
        for (const k of fileKeys) {
          if (Object.prototype.hasOwnProperty.call(item, k) && item[k]) {
            url = item[k];
            break;
          }
        }

        // If backend returns attachments array/object
        if (
          !url &&
          Array.isArray(item.attachments) &&
          item.attachments[i - 1]
        ) {
          const a = item.attachments[i - 1];
          url = a.url ?? a.file ?? a.file_url ?? a.attachment_url ?? url;
          desc = desc || a.description || a.desc || "";
        }

        newFields[`attachments${i}`] = {
          description: desc ?? "",
          file: null,
          url: url ?? null,
        };
      }

      // Approving authority signature preview
      newFields.approvingAuthoritySignature = null;
      newFields.approvingAuthoritySignatureUrl =
        item.approving_authority_file ??
        item.approving_authority_file_url ??
        item.approvingAuthorityFileUrl ??
        item.approving_authority_file_path ??
        null;

      // Projects 1..5 mapping (projectNameX, locationX, capacityX, projectCompletionYearX, projectCurrentStatusX)
      for (let i = 1; i <= 5; i++) {
        newFields[`projectName${i}`] =
          item[`project_name_${i}`] ??
          item[`projectName${i}`] ??
          item[`project_name${i}`] ??
          item[`project${i}_name`] ??
          "";
        newFields[`location${i}`] =
          item[`location_${i}`] ??
          item[`location${i}`] ??
          item[`project_location_${i}`] ??
          "";
        newFields[`capacity${i}`] =
          item[`capacity_${i}`] ?? item[`capacity${i}`] ?? "";
        newFields[`projectCompletionYear${i}`] =
          item[`project_completion_year_${i}`] ??
          item[`projectCompletionYear${i}`] ??
          item[`completion_year_${i}`] ??
          "";
        newFields[`projectCurrentStatus${i}`] =
          item[`project_current_status_${i}`] ??
          item[`projectCurrentStatus${i}`] ??
          item[`status_${i}`] ??
          "";
      }

      // Copy any remaining backend *_2024 or *_2023 keys into newFields (stringified) if not set above
      Object.keys(item).forEach((k) => {
        if (/_2024$/.test(k) || /_2023$/.test(k)) {
          if (!Object.prototype.hasOwnProperty.call(newFields, k)) {
            newFields[k] = safeStr(item[k]);
          }
        }
      });

      // Carry over created/updated meta if present
      if (item.created_at) newFields.created_at = item.created_at;
      if (item.updated_at) newFields.updated_at = item.updated_at;
      if (item.form_id) newFields.form_id = item.form_id;
      if (item.id) newFields.id = item.id;

      // Final merge into state (immutable)
      setFormData((prev) => ({ ...prev, ...newFields }));

      console.log("prefillFormData applied; pkId:", item.id ?? item.pk);
      return item;
    } catch (err) {
      console.error("prefillFormData error:", err);
      throw err;
    }
  }

  // below validate const useeffect

  const [error, setError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [copyApplicantData, setCopyApplicantData] = useState(false);
  const location = useLocation();
  console.log(
    "RegistrationExploration rendered, location.state:",
    location.state
  ); // Debug log
  const awardTitle =
    location.state?.awardTitle || "Goal Net Zero Company of the Year";

  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (prev[field]) {
        const { [field]: omitted, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let applicableMaxLength = FIELD_MAX_LENGTH;
    if ([1, 2, 3, 4].includes(currentStep)) {
      let applicableMaxLength = FIELD_MAX_LENGTH;
      if (name === "companyProfile")
        applicableMaxLength = COMPANY_PROFILE_MAX_LENGTH;
      else if (name === "comment") applicableMaxLength = COMMENT_MAX_LENGTH;
      else if (
        [
          "activityName1",
          "activityName2",
          "activityName3",
          "activityName4",
          "activityName5",
          "plannedActivities1",
          "plannedActivities2",
          "plannedActivities3",
          "plannedActivities4",
          "plannedActivities5",
          "actualProgress1",
          "actualProgress2",
          "actualProgress3",
          "actualProgress4",
          "actualProgress5",
        ].includes(name)
      ) {
        applicableMaxLength = 50;
      }

      if (typeof value === "string") {
        if (name === "companyProfile") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 300) {
            alert("Maximum 300 words allowed.");
            return;
          }
        } else if (name === "comment") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 200) {
            alert("Maximum 200 words allowed in Comments.");
            return;
          }
        } else if (name === "activityName1") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed ");
            return;
          }
        } else if (name === "activityName2") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed");
            return;
          }
        } else if (name === "activityName3") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed");
            return;
          }
        } else if (name === "activityName4") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed ");
            return;
          }
        } else if (name === "activityName5") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed");
            return;
          }
        } else if (name === "plannedActivities1") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed");
            return;
          }
        } else if (name === "plannedActivities2") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed");
            return;
          }
        } else if (name === "plannedActivities3") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed");
            return;
          }
        } else if (name === "plannedActivities4") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed");
            return;
          }
        } else if (name === "plannedActivities5") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 50) {
            alert("Maximum 50 words are allowed");
            return;
          }
        } else if (value.length > applicableMaxLength) {
          alert(`Value cannot exceed ${applicableMaxLength} characters.`);
          return;
        }
      }
    }

    // Name validation
    if (["Organisationname", "authorityName", "contact_name"].includes(name)) {
      const isValid = /^[A-Za-z\s]*$/.test(value);
      if (!isValid && value !== "") {
        alert("Only letters and spaces are allowed.");
        return;
      }
    }

    // Phone validation
    if (name === "authorityPhone" || name === "contact_phone") {
      const numericValue = value.replace(/\D/g, "").slice(0, PHONE_MAX_LENGTH);
      if (numericValue.length > PHONE_MAX_LENGTH) {
        alert("Phone number must not exceed 10 digits.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "Organisationname" && value && currentStep === 1) setError("");
    if (name === "mailingAddress" && value.trim() && currentStep === 1)
      setError("");
    if (name === "authorityName" && value && currentStep === 2) setError("");
    if (name === "authorityEmail" && value && currentStep === 2) setError("");
  };

  const handleCopyApplicantToggle = (e) => {
    const checked = e.target.checked;
    // setCopyApplicantData(checked);

    const model = {
      contact_name: "",
      contact_phone: "",
      contact_email: "",
    };

    if (checked) {
      const userInfo = JSON.parse(sessionStorage.getItem("user_info"));
      if (userInfo) {
        model.contact_name = userInfo.applicant_name;
        model.contact_phone = userInfo.applicant_phone;
        model.contact_email = userInfo.email;
      }
    }

    setFormData((prev) => ({ ...prev, ...model }));

    clearFieldError("contact_name");
    clearFieldError("contact_phone");
    clearFieldError("contact_email");
  };

  const handleAttachmentChange = (key, field, value, event = null) => {
    if (field === "file" && value) {
      const file = value;
      const maxSizeInBytes = 5 * 1024 * 1024;

      if (!["image/jpeg", "image/png", "application/pdf"].includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for attachments.");
        if (event) event.target.value = null;
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for attachments.");
        if (event) event.target.value = null;
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const handleApprovingAuthorityChange = (files) => {
    const file = files[0];
    if (file) {
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for the signature.");
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for the signature.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        approvingAuthoritySignature: file,
      }));
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.Organisationname) {
        alert("Organisation name is required.");
        return;
      }
      if (!formData.mailingAddress.trim()) {
        alert("Mailing address is required.");
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.authorityName) {
        alert("Authority name is required.");
        return;
      }
      if (!formData.authorityTitle) {
        alert("Authority Designation is required.");
        return;
      }
      if (
        !formData.authorityEmail ||
        !emailRegex.test(formData.authorityEmail)
      ) {
        alert("Please enter a valid Authority email.");
        return;
      }
      // if (!formData.authorityPhone || !phoneRegex.test(formData.authorityPhone)) {
      //     alert('Authority phone must be exactly 10 digits.');
      //     return;
      // }
      if (!formData.authorityLandline) {
        alert("Authority Landline is required.");
        return;
      }
      if (!formData.contact_name) {
        alert("contact name is required.");
        return;
      }
      if (formData.contact_email && !emailRegex.test(formData.contact_email)) {
        alert("Please enter a valid Contact email.");
        return;
      }
      if (
        formData.contact_phone &&
        !phoneRegex.test(formData.contact_phone) &&
        !copyApplicantData
      ) {
        alert("Contact phone must be exactly 10 digits.");
        return;
      }
      if (!formData.companyProfile) {
        alert("Company Profile is required.");
        return;
      }
    }
    if (hasEmptyFieldsInCurrentStep(currentStep)) {
      if (!window.confirm("Data not entered, If you wish to continue?")) {
        return;
      }
    }

    setError("");
    if (currentStep < 5) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };



   const viewFile =  async (serverPath) => {
    // "https://award-backend.fipi.org.in/media/
    const extractPath = serverPath.split('/').filter(
  (val) =>
    val !== "" && // remove empty strings
    val !== "http:" &&
    val !== "https:" &&
    val !== "media" &&
    !val.startsWith("award") &&
    !val.startsWith("localhost")
).join('/');
    
    downloadFile(extractPath)
  }
  // Save draft handler
  const handleSaveDraft = () => {
    localStorage.setItem("registrationGNZDraft", JSON.stringify({ formData }));
    handleSubmit(null);
    alert("Draft Saved!");
  };

  //Final Submit button

  const finalSubmit = () => {
    handleSubmit(null, "submited");
  };

  // Submit form handler
  // Replace your existing handleSubmit with this
  async function handleSubmit(event, clickType = "draft") {
    if (event && event.preventDefault) event.preventDefault();
    console.log("Saving Goal Net Zero form (clickType):", clickType);

    // local copy
    const payload = { ...formData };

    // merge user_info if available
    try {
      const raw =
        sessionStorage.getItem("user_info") ||
        localStorage.getItem("user_info");
      if (raw) {
        const u = JSON.parse(raw);
        payload.userid = payload.userid || (u.id ? String(u.id) : "");
        payload.firstname = payload.firstname || u.first_name || "";
        payload.lastname = payload.lastname || u.last_name || "";
        payload.company_name =
          payload.company_name || u.organisation_name || "";
        payload.Organisationname =
          payload.Organisationname || u.organisation_name || "";
      }
    } catch (e) {
      console.warn("user_info parse failed:", e);
    }

    try {
      const fd = new FormData();

      // Identity/contact
      fd.append(
        "organisation_name",
        payload.Organisationname || payload.organisation_name || ""
      );
      fd.append(
        "firstname",
        payload.firstname || payload.firstname || ""
      );
      fd.append(
        "lastname",
        payload.lastname || payload.lastname || ""
      );
      fd.append("category", payload.category || "");
      fd.append(
        "company_name",
        payload.companyName || payload.company_name || ""
      );
      fd.append(
        "mailing_address",
        payload.mailingAddress || payload.mailing_address || ""
      );

      // Authority
      fd.append(
        "authority_name",
        payload.authorityName || payload.authority_name || ""
      );
      fd.append(
        "authority_title",
        payload.authorityTitle || payload.authority_title || ""
      );
      fd.append(
        "authority_phone",
        payload.authorityPhone || payload.authority_phone || ""
      );
      fd.append(
        "authorityLandline",
        payload.authorityLandline || payload.authority_landline || ""
      );
      fd.append(
        "authority_email",
        payload.authorityEmail || payload.authority_email || ""
      );

      // Contacts
      fd.append("contact_name", payload.contact_name || "");
      fd.append("contact_phone", payload.contact_phone || "");
      fd.append("contact_email", payload.contact_email || "");

      // Narrative/meta
      fd.append(
        "company_profile",
        payload.companyProfile || payload.company_profile || ""
      );
      fd.append("comment", payload.comment || "");
      fd.append("declaration", payload.declaration ? "true" : "false");

      fd.append("activityName1", payload.activityName1 || "");
      fd.append("plannedActivities1", payload.plannedActivities1 || "");
      fd.append("actualProgress1", payload.actualProgress1 || "");
      fd.append("activityName2", payload.activityName2 || "");
      fd.append("plannedActivities2", payload.plannedActivities2 || "");
      fd.append("actualProgress2", payload.actualProgress2 || "");
      fd.append("activityName3", payload.activityName3 || "");
      fd.append("plannedActivities3", payload.plannedActivities3 || "");
      fd.append("actualProgress3", payload.actualProgress3 || "");
      fd.append("activityName4", payload.activityName4 || "");
      fd.append("plannedActivities4", payload.plannedActivities4 || "");
      fd.append("actualProgress4", payload.actualProgress4 || "");
      fd.append("activityName5", payload.activityName5 || "");
      fd.append("plannedActivities5", payload.plannedActivities5 || "");
      fd.append("actualProgress5", payload.actualProgress5 || "");

      // meta
      fd.append("form_id", formId || "");
      fd.append("form_mode", clickType || payload.form_mode || "draft");
      if (PkId > 0) fd.append("id", String(PkId));

      // file
      if (payload.approvingAuthoritySignature instanceof File) {
        fd.append(
          "approving_authority_file",
          payload.approvingAuthoritySignature
        );
      }

      // quantitative (2024/2023)
      const qMap = {
        netZeroTarget: "net_zero_target",
        carbonEmitted: "carbon_emitted",
        energyConsumed: "energy_consumed",
        annualRevenue: "annual_revenue",
        capexWind: "capex_wind",
        capexSolar: "capex_solar",
        capexOtherRE: "capex_other_re",
        rePowerProd: "re_power_prod",
        powerConsumed: "power_consumed",
        gh2Investment: "gh2_investment",
        gh2Production: "gh2_production",
        treePlantation: "tree_plantation",
        carbonCapture: "carbon_capture",
        ccsCapex: "ccs_capex",
        carbonCaptured: "carbon_captured",
      };
      Object.entries(qMap).forEach(([front, base]) => {
        fd.append(`${base}_2024`, payload[`${front}2024`] ?? "");
        fd.append(`${base}_2023`, payload[`${front}2023`] ?? "");
      });

      // // 5 flat technology rows on parent model
      // for (let i = 1; i <= 5; i++) {
      //   fd.append(`activityName${i}`, payload[`activityName${i}`] || "");
      //   fd.append(`plannedActivities${i}`, payload[`plannedActivities${i}`] || "");
      //   fd.append(`actualProgress${i}`, payload[`actualProgress${i}`] || "");
      // }

      // // optional nested array for GNZTechnology (child rows)
      // const technologies = [];
      // for (let i = 1; i <= 5; i++) {
      //   const name = payload[`activityName${i}`] || "";
      //   const plan = payload[`plannedActivities${i}`] || "";
      //   const prog = payload[`actualProgress${i}`] || "";
      //   if (name || plan || prog) {
      //     technologies.push({
      //       activity_name: name,
      //       planned_activities: plan,
      //       actual_progress: prog,
      //     });
      //   }
      // }
      // if (technologies.length > 0) {
      //   fd.append("technologies", JSON.stringify(technologies));
      // }

      // attachments
      for (let i = 1; i <= 4; i++) {
        const slot = payload[`attachments${i}`] || {
          description: "",
          file: null,
        };
        fd.append(`attachments${i}_desc`, slot.description || "");
        if (slot.file instanceof File) {
          fd.append(`attachments${i}`, slot.file);
        }
      }

      // send
      const token =
        sessionStorage.getItem("access") || localStorage.getItem("access");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await apiClient.post("/goal-net-zero/", fd, { headers });

      // refresh local UI
      await prefillFormData();
      return res;
    } catch (err) {
      console.error("Goal Net Zero submit error:", err);
      const serverMsg = err?.response?.data ?? err?.message ?? String(err);
      alert("Submission failed: " + JSON.stringify(serverMsg));
      throw err;
    }
  }

  // Helper function
  function toSnake(str) {
    return str.replace(/([A-Z])/g, "_$1").toLowerCase();
  }

  const handlePrint = () => {
    const printContent = `
    <div style="font-family: Arial, sans-serif; padding: 20px;">
      <h1 style="text-align: center; color: #1e40af;">${awardTitle}</h1>
      <h2>Organization & Contact Details</h2>
      <p><strong>Organisation Name:</strong> ${
        formData.Organisationname || ""
      }</p>
      <p><strong>Postal Address:</strong> ${formData.mailingAddress || ""}</p>
      <p><strong>Approving Authority Name:</strong> ${
        formData.authorityName || ""
      }</p>
      <p><strong>Approving Authority Designation:</strong> ${
        formData.authorityTitle || ""
      }</p>
      <p><strong>Approving Authority Landline:</strong> ${
        formData.authorityLandline || ""
      }</p>
      <p><strong>Approving Authority Phone:</strong> ${
        formData.authorityPhone || ""
      }</p>
      <p><strong>Approving Authority Email:</strong> ${
        formData.authorityEmail || ""
      }</p>

      <h2>Nodal Official Contact Details:</h2>
      <p><strong>Contact Name:</strong> ${formData.contact_name || ""}</p>
      <p><strong>Contact Phone:</strong> ${formData.contact_phone || ""}</p>
      <p><strong>Contact Email:</strong> ${formData.contact_email || ""}</p>
      <p><strong>Brief write up on company’s profile:</strong> ${
        formData.companyProfile || ""
      }</p>

      <h2>Quantitative Information</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;">S.No</th>
            <th style="border: 1px solid #000; padding: 8px;">Particulars</th>
            <th style="border: 1px solid #000; padding: 8px;">2024-25</th>
            <th style="border: 1px solid #000; padding: 8px;">2023-24</th>
          </tr>
        </thead>
        <tbody>
          ${part1
            .map(
              ({ num, title, key2024, key2023 }) => `
            <tr>
              <td style="border: 1px solid #000; padding: 8px;">${num}</td>
              <td style="border: 1px solid #000; padding: 8px;">${title}</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[key2024] || ""
              }</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[key2023] || ""
              }</td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>

      <h2>New Technologies Adopted</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;">S.No</th>
            <th style="border: 1px solid #000; padding: 8px;">Activity Name</th>
            <th style="border: 1px solid #000; padding: 8px;">Planned Activities</th>
            <th style="border: 1px solid #000; padding: 8px;">Actual Progress</th>
          </tr>
        </thead>
        <tbody>
          ${[1, 2, 3, 4, 5]
            .map(
              (i) => `
            <tr>
              <td style="border: 1px solid #000; padding: 8px;">${i}</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`activityName${i}`] || ""
              }</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`plannedActivities${i}`] || ""
              }</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`actualProgress${i}`] || ""
              }</td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>

      <h2>Attachments</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px; width:10px">S.No</th>
            <th style="border: 1px solid #000; padding: 8px;">Description</th>
            <th style="border: 1px solid #000; padding: 8px;">File Name</th>
          </tr>
        </thead>
        <tbody>
          ${[1, 2, 3, 4]
            .map(
              (i) => `
            <tr>
              <td style="border: 1px solid #000; padding: 8px;">${i}</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`attachments${i}`].description || ""
              }</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`attachments${i}`].file?.name || ""
              }</td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>

      <h2>Comments</h2>
      <p>${formData.comment || ""}</p>

      <h2>Declaration</h2>
      <p>I declare that the information submitted is true and complete.</p>
      <br/>
      <p><strong>Approving Authority Name:</strong> ${
        formData.authorityName || ""
      }</p>
      <p><strong>Approving Authority Designation:</strong> ${
        formData.authorityTitle || ""
      }</p>
      <p><strong>Signed on:</strong> ${
        formData.signatureDate || new Date().toLocaleDateString()
      }</p>
      <p><strong>Approving Authority Signature:</strong></p>
    </div>
  `;

    const printWindow = window.open("", "_blank", "height=600,width=800");
    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>${awardTitle || "Form Print"}</title>
      </head>
      <body>
        ${printContent}
      </body>
    </html>
  `);
    printWindow.document.close();
    printWindow.print();
  };

  const part1 = [
    {
      num: 1,
      title: "Net Zero (Scope – I & II) – Target Year",
      key2024: "netZeroTarget2024",
    },
    {
      num: 2,
      title: "Total Carbon Emitted (Scope – I & II) (tCO2e) ",
      key2024: "carbonEmitted2024",
      key2023: "carbonEmitted2023",
    },
    {
      num: 3,
      title: "Total Energy Consumed (GJ)",
      key2024: "energyConsumed2024",
      key2023: "energyConsumed2023",
    },
    {
      num: 4,
      title: "Annual Revenue (INR Crore)",
      key2024: "annualRevenue2024",
      key2023: "annualRevenue2023",
    },
    {
      num: 5,
      title: "Capex for Wind Energy Generation (INR Crore)",
      key2024: "capexWind2024",
      key2023: "capexWind2023",
    },
    {
      num: 6,
      title: "Capex for Solar Energy Generation (INR Crore) ",
      key2024: "capexSolar2024",
      key2023: "capexSolar2023",
    },
    {
      num: 7,
      title: "Capex for Other RE Generation (INR Crore) ",
      key2024: "capexOtherRE2024",
      key2023: "capexOtherRE2023",
    },
    {
      num: 8,
      title: "Total Renewable Energy Power Production (MW) ",
      key2024: "rePowerProd2024",
      key2023: "rePowerProd2023",
    },
    {
      num: 9,
      title: "Total Power Consumption (MW)",
      key2024: "powerConsumed2024",
      key2023: "powerConsumed2023",
    },
    {
      num: 10,
      title:
        "Investment for GH2 production/Transportation/Distribution/Storage (INR Crore) ",
      key2024: "gh2Investment2024",
      key2023: "gh2Investment2023",
    },
    {
      num: 11,
      title: "GH2 Production (MT)",
      key2024: "gh2Production2024",
      key2023: "gh2Production2023",
    },
    {
      num: 12,
      title: "Tree Plantation (Nos.)",
      key2024: "treePlantation2024",
      key2023: "treePlantation2023",
    },
    // { num: 13, title: 'Initiatives/ Project undertaken for Carbon Capture ', },
    {
      num: 13,
      title: "Capex for CCS/CCUS Projects (INR Crore) ",
      key2024: "ccsCapex2024",
      key2023: "ccsCapex2023",
    },
    {
      num: 14,
      title: "Carbon Captured (MT)",
      key2024: "carbonCaptured2024",
      key2023: "carbonCaptured2023",
    },
  ];

  const hasEmptyFieldsInCurrentStep = (step) => {
    if (step === 3) {
      // Skip num 1 row
      const step3Sections = part1.filter(
        (section) => section.num >= 1 && section.num <= 9 && section.num !== 1
      );
      for (const section of step3Sections) {
        if (
          (section.key2024 &&
            !formData[section.key2024] &&
            formData[section.key2024] !== 0) ||
          (section.key2023 &&
            !formData[section.key2023] &&
            formData[section.key2023] !== 0)
        ) {
          return true;
        }
      }
    }

    if (step === 4) {
      // Skip num 13 row
      const step4Sections = part1.filter(
        (section) =>
          section.num >= 10 && section.num <= 15 && section.num !== 13
      );
      for (const section of step4Sections) {
        if (
          (section.key2024 &&
            !formData[section.key2024] &&
            formData[section.key2024] !== 0) ||
          (section.key2023 &&
            !formData[section.key2023] &&
            formData[section.key2023] !== 0)
        ) {
          return true;
        }
      }

      // Check all activities inputs
      for (let i = 1; i <= 5; i++) {
        const fields = [
          `activityName${i}`,
          `plannedActivities${i}`,
          `actualProgress${i}`,
        ];
        for (const f of fields) {
          if (!formData[f] && formData[f] !== 0) {
            return true;
          }
        }
      }

      // Comment check
      if (!formData.comment || formData.comment.trim() === "") {
        return true;
      }
    }

    return false;
  };

  const renderStepContent = () => {
    const progress = ((currentStep - 1) / 4) * 100;
    return (
      <div className="form-step">
        <div className="progress-bar">
          <div className="progress" style={{ width: `${progress}%` }}></div>
        </div>

        {currentStep === 1 && (
          <div>
            <h3 className="step-title">Step 1: Organization Details</h3>
            <div className="form-group">
              <label>
                Organisation Name
                {/* <span className="text-red">*</span> */}
              </label>
              <input
                type="text"
                name="Organisationname"
                maxLength={FIELD_MAX_LENGTH}
                value={formData.Organisationname}
                // onChange={handleChange}
                disabled={true}
                onChange={(e) => handleChange(e)}
                className={`form-input ${
                  !formData.Organisationname && currentStep === 1
                    ? "has-error"
                    : ""
                }`}
              />
              {!formData.Organisationname && currentStep === 1 && (
                <span className="error-tooltip">
                  Organisation name is required
                </span>
              )}
            </div>
            <div className="form-group">
              <label>
                Postal Address <span className="text-red">*</span>
              </label>
              <textarea
                name="mailingAddress"
                maxLength={FIELD_MAX_LENGTH}
                value={formData.mailingAddress}
                onChange={handleChange}
                className={`form-textarea ${
                  !formData.mailingAddress.trim() && currentStep === 1
                    ? "has-error"
                    : ""
                }`}
                rows={3}
                placeholder="Enter Postal address"
              />
              {!formData.mailingAddress.trim() && currentStep === 1 && (
                <span className="error-tooltip">
                  Mailing address is required
                </span>
              )}
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <h3 className="step-title">
              Step 2: Approving Authority & Contact
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="step-section">
                <h4>Approving Authority</h4>
                <p className="note">
                  Approving authority should be concerned Director /Board level
                  executive.{" "}
                </p>
                <div className="form-group">
                  <label>
                    Name <span className="text-red">*</span>
                  </label>
                  <input
                    type="text"
                    name="authorityName"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityName}
                    onChange={handleChange}
                    className={`form-input ${
                      !formData.authorityName && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    placeholder="Name"
                  />
                  {!formData.authorityName && currentStep === 2 && (
                    <span className="error-tooltip">Name is required</span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Designation:
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    type="text"
                    name="authorityTitle"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityTitle}
                    onChange={handleChange}
                    className={`form-input ${
                      !formData.authorityTitle && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    placeholder="Designation"
                  />
                  {!formData.authorityTitle && currentStep === 2 && (
                    <span className="error-tooltip">
                      Designation is required
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Landline:{" "}
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityLandline"
                    type="number"
                    name="authorityLandline"
                    value={formData.authorityLandline}
                    onChange={handleChange}
                    // onBlur={(e) => handleBlur('authorityLandline', e.target.value)}
                    className={`form-input ${
                      !formData.authorityLandline && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    placeholder="Landline number"
                    aria-required="true"
                    aria-describedby={
                      fieldErrors.authorityLandline
                        ? "authorityLandline-error"
                        : undefined
                    }
                  />
                  {!formData.authorityLandline && currentStep === 2 && (
                    <span className="error-tooltip">
                      Authority Landline Number is required
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Mobile:{" "}
                    {/* <span className="text-red" aria-hidden="true"></span> */}
                  </label>
                  <input
                    id="authorityPhone"
                    type="tel"
                    name="authorityPhone"
                    value={formData.authorityPhone}
                    onChange={handleChange}
                    // onBlur={(e) => handleBlur('authorityPhone', e.target.value)}
                    className={`form-input`}
                    placeholder="Phone number"
                    maxLength={PHONE_MAX_LENGTH}
                    aria-required="true"
                    // aria-describedby={fieldErrors.authorityPhone ? 'authorityPhone-error' : undefined}
                  />
                </div>

                <div className="form-group">
                  <label>
                    Email <span className="text-red">*</span>
                  </label>
                  <input
                    type="email"
                    name="authorityEmail"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityEmail}
                    onChange={handleChange}
                    className={`form-input ${
                      !formData.authorityEmail && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    placeholder="E-mail address"
                  />
                  {!formData.authorityEmail && currentStep === 2 && (
                    <span className="error-tooltip">Email is required</span>
                  )}
                </div>
              </div>
              <div className="step-section">
                <h4>
                  Contacts (Nodal Officials) <span className="text-red">*</span>
                </h4>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="copyApplicantData"
                      //   checked={copyApplicantData}
                      onChange={handleCopyApplicantToggle}
                      className="form-checkbox"
                    />{" "}
                    Same as applicant
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="contact_name">
                    Name{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_name"
                    name="contact_name"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_name}
                    onChange={handleChange}
                    placeholder="Contact name"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      !formData.contact_name && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    aria-describedby="contact_name-error"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_phone">
                    Phone:
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    maxLength={PHONE_MAX_LENGTH}
                    value={formData.contact_phone}
                    onChange={handleChange}
                    aria-describedby="contact_phone-error"
                    disabled={copyApplicantData}
                    placeholder="10-digit phone number"
                    className={`form-input ${
                      !formData.contact_phone && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_email">Email</label>
                  <input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="Contact email"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      fieldErrors.contact_email ? "has-error" : ""
                    }`}
                    aria-describedby="contact_email-error"
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Brief write up on company’s profile </label>
              <p className="note">(within 300 words)</p>
              <textarea
                name="companyProfile"
                // maxLength={COMPANY_PROFILE_MAX_LENGTH}
                value={formData.companyProfile}
                onChange={handleChange}
                className="form-textarea"
                rows={8}
              />
            </div>
          </div>
        )}
        {currentStep === 3 && (
          <div>
            <h3 className="step-title">
              Step 3: Quantitative Information -Part 1
            </h3>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Particulars</th>
                  <th colSpan="2" style={{ alignItems: "center" }}>
                    Target Year
                  </th>
                </tr>
                {part1.slice(0, 1).map((item) => (
                  <tr>
                    <td>1</td>
                    <td>Net Zero (Scope – I & II) – Target Year</td>
                    <td colSpan="2">
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="text" // keep text so browser spinners don't show
                        name={item.key2024}
                        value={formData[item.key2024] || ""}
                        onChange={(e) => {
                          let val = e.target.value;

                          // ✅ allow only digits and one decimal point
                          val = val.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange({
                            target: {
                              name: e.target.name,
                              value: val,
                            },
                          });
                        }}
                        onKeyDown={(e) => {
                          if (
                            ["-", "+", "e", "E"].includes(e.key) ||
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()}
                        className="form-input"
                        inputProps={{
                          inputMode: "decimal",
                          pattern: "[0-9]*\\.?[0-9]*",
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </thead>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Particulars</th>
                  <th>2024-25</th>
                  <th>2023-24</th>
                </tr>
              </thead>
              <tbody>
                {part1.slice(1, 9).map((item) => (
                  <tr key={item.num}>
                    <td className="sno-cell">{item.num}</td>
                    <td className="label-cell">{item.title}</td>
                    <td>
                      {["1"].includes(item.num) ? (
                        <span>{item.key2024}</span>
                      ) : item.key2024 ? (
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="text" // use text to prevent browser spinners
                          name={item.key2024}
                          value={formData[item.key2024] || ""}
                          onChange={(e) => {
                            let val = e.target.value;

                            // Allow only digits and one decimal point
                            val = val.replace(/[^0-9.]/g, "");
                            const parts = val.split(".");
                            if (parts.length > 2) {
                              val = parts[0] + "." + parts.slice(1).join("");
                            }

                            handleChange({
                              target: {
                                name: e.target.name,
                                value: val,
                              },
                            });
                          }}
                          onKeyDown={(e) => {
                            // block '-', '+', 'e', 'E', arrow keys
                            if (
                              ["-", "+", "e", "E"].includes(e.key) ||
                              e.key === "ArrowUp" ||
                              e.key === "ArrowDown"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()} // disable mouse scroll increment
                          className="form-input"
                          inputProps={{
                            inputMode: "decimal", // mobile numeric keypad
                            pattern: "[0-9]*\\.?[0-9]*", // digits + optional decimal
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {["1"].includes(item.num) ? (
                        <span>{item.key2023}</span>
                      ) : item.key2023 ? (
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="text" // prevents browser spinners
                          name={item.key2023}
                          value={formData[item.key2023] || ""}
                          onChange={(e) => {
                            let val = e.target.value;

                            // Allow only digits and a single decimal point
                            val = val.replace(/[^0-9.]/g, "");
                            const parts = val.split(".");
                            if (parts.length > 2) {
                              val = parts[0] + "." + parts.slice(1).join("");
                            }

                            handleChange({
                              target: {
                                name: e.target.name,
                                value: val,
                              },
                            });
                          }}
                          onKeyDown={(e) => {
                            // block '-', '+', 'e', 'E', arrow keys
                            if (
                              ["-", "+", "e", "E"].includes(e.key) ||
                              e.key === "ArrowUp" ||
                              e.key === "ArrowDown"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()} // prevent mouse scroll increment
                          className="form-input"
                          inputProps={{
                            inputMode: "decimal", // numeric keypad on mobile
                            pattern: "[0-9]*\\.?[0-9]*", // digits with optional decimal
                          }}
                        />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {currentStep === 4 && (
          <div>
            <h3 className="step-title">
              Step 4: Quantitative Information -Part 2
            </h3>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Particulars</th>
                  <th>2024-25</th>
                  <th>2023-24</th>
                </tr>
              </thead>
              <tbody>
                {part1.slice(9, 15).map((item) => (
                  <tr key={item.num}>
                    <td className="sno-cell">{item.num}</td>
                    <td className="label-cell">{item.title}</td>
                    <td>
                      {["13"].includes(item.num) ? (
                        <span>{item.key2024}</span>
                      ) : item.key2024 ? (
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="text"
                          name={item.key2024}
                          value={formData[item.key2024] || ""}
                          onChange={(e) => {
                            let val = e.target.value;

                            val = val.replace(/[^0-9.]/g, "");
                            const parts = val.split(".");
                            if (parts.length > 2) {
                              val = parts[0] + "." + parts.slice(1).join("");
                            }

                            handleChange({
                              target: {
                                name: e.target.name,
                                value: val,
                              },
                            });
                          }}
                          onKeyDown={(e) => {
                            // block '-', '+', 'e', 'E', arrow keys
                            if (
                              ["-", "+", "e", "E"].includes(e.key) ||
                              e.key === "ArrowUp" ||
                              e.key === "ArrowDown"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()} // prevent mouse scroll increment
                          className="form-input"
                          inputProps={{
                            inputMode: "decimal", // numeric keypad on mobile
                            pattern: "[0-9]*\\.?[0-9]*", // digits with optional decimal
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {["13"].includes(item.num) ? (
                        <span>{item.key2023}</span>
                      ) : item.key2023 ? (
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="text" // prevents browser spinners
                          name={item.key2023}
                          value={formData[item.key2023] || ""}
                          onChange={(e) => {
                            let val = e.target.value;

                            // Allow only digits and a single decimal point
                            val = val.replace(/[^0-9.]/g, "");
                            const parts = val.split(".");
                            if (parts.length > 2) {
                              val = parts[0] + "." + parts.slice(1).join("");
                            }

                            handleChange({
                              target: {
                                name: e.target.name,
                                value: val,
                              },
                            });
                          }}
                          onKeyDown={(e) => {
                            // block '-', '+', 'e', 'E', arrow keys
                            if (
                              ["-", "+", "e", "E"].includes(e.key) ||
                              e.key === "ArrowUp" ||
                              e.key === "ArrowDown"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()} // prevent mouse scroll increment
                          className="form-input"
                          inputProps={{
                            inputMode: "decimal", // numeric keypad on mobile
                            pattern: "[0-9]*\\.?[0-9]*", // digits with optional decimal
                          }}
                        />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Activities Table */}
            <table className="quant-table">
              <thead>
                <th></th>
                <th>
                  Major activities planned as per Net Zero Target for 2024-25
                  (Max. top 5 Activities) (50 words each against each cell)
                </th>
                <th></th>
                <th></th>
              </thead>
            </table>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Activity Name</th>
                  <th>Planned Activities</th>
                  <th>Actual Progress</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td className="sno-cell">{i}</td>
                    <td>
                      <input
                        type="text"
                        name={`activityName${i}`}
                        // maxLength={TEXTAREA_MAX_LENGTH}
                        value={formData[`activityName${i}`] || ""}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name={`plannedActivities${i}`}
                        // maxLength={TEXTAREA_MAX_LENGTH}
                        value={formData[`plannedActivities${i}`] || ""}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name={`actualProgress${i}`}
                        maxLength={TEXTAREA_MAX_LENGTH}
                        value={formData[`actualProgress${i}`] || ""}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Comments Section */}
            <div className="step-section" style={{ marginTop: "20px" }}>
              <div className="form-group">
                <label htmlFor="comment">Comments</label>
                <textarea
                  id="comment"
                  name="comment"
                  // maxLength={COMMENT_MAX_LENGTH}
                  value={formData.comment || ""}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Comments in (200 words) against input parameter, if any"
                  rows={4}
                />
                {fieldErrors.comment && (
                  <span
                    className="error-tooltip"
                    id="comment-error"
                    role="alert"
                  >
                    {fieldErrors.comment}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {currentStep === 5 && (
          <div className="form-step">
            <h3 className="step-title">Step 5: Attachments & Declaration</h3>
            <div className="form-group">
              <label>List of Attachments (Optional):</label>
              <table className="quant-table">
                <thead>
                  <tr>
                    <th>S. No.</th>
                    <th>Description</th>
                    <th>Upload (only jpg, png, pdf, max 5 MB each)</th>
                  </tr>
                </thead>
                {/* <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key];
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            maxLength={FIELD_MAX_LENGTH}
                            value={attachment.description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Enter description"
                            className="form-input"
                          />
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className="form-input mt-4"
                          />
                          {attachment.file && (
                            <p className="file-name">
                              Selected file: {attachment.file.name}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody> */}
                <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key] || {
                      description: "",
                      file: null,
                    };
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            value={attachment.description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Enter description"
                            maxLength={FIELD_MAX_LENGTH}
                            className="form-input"
                          />
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className="form-input mt-4"
                          />
                          {attachment.file && (
                            <p className="file-name">
                              Selected file: {attachment.file.name}
                            </p>
                          )}
                          {attachment.url && !attachment.file && (
                            <p className="file-name">
                              Uploaded file:{" "}
                              <button
                                type="button"
                                onClick={() => viewFile(attachment.url)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  color: "#06c",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                              >
                                View
                              </button>
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="form-group">
              <label>
                Print the completed application form and upload it with the
                signature of the approving authority. Without signature of the
                approving authority, application will not be considered valid.
                <span className="text-red">*</span>
              </label>
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="btn btn-outline"
                >
                  Print Preview
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="approving_authority_file">
                Upload Document with Approving Authority Signature
                (Director/Board Level)
                <span aria-hidden="true" className="text-red">
                  *
                </span>
                :
              </label>
              <input
                type="file"
                id="approving_authority_file"
                accept=".jpg,.png,.pdf"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files.length > 0) {
                    const confirmed = window.confirm(
                      "Are you sure the form is approved and signed by approving authority?"
                    );
                    if (!confirmed) {
                      // If user clicks "Cancel", clear the input
                      e.target.value = "";
                      return;
                    }
                    handleApprovingAuthorityChange(files);
                  }
                }}
                className={`form-input mt-4 ${
                  fieldErrors.approving_authority_file ? "has-error" : ""
                }`}
                aria-describedby="approving_authority_file-error"
                required
              />
              {formData.approving_authority_file && (
                <p className="file-name">
                  Selected file: {formData.approving_authority_file.name}
                </p>
              )}
              {formData.approvingAuthoritySignatureUrl &&
                !formData.approvingAuthoritySignature && (
                  <p className="file-name">
                    Uploaded file:{" "}
                    <button
                      type="button"
                      onClick={() =>
                        viewFile(formData.approvingAuthoritySignatureUrl)
                      }
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        color: "#06c",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      View
                    </button>
                  </p>
                )}
              {fieldErrors.approving_authority_file && (
                <span
                  className="error-tooltip"
                  id="approving_authority_file-error"
                  role="alert"
                >
                  {fieldErrors.approving_authority_file}
                </span>
              )}
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="declaration"
                  checked={formData.declaration}
                  onChange={handleChange}
                  className="form-checkbox"
                />
                I declare that the information submitted is true and complete.
              </label>
            </div>
          </div>
        )}
      </div>
    );
  };
  useEffect(() => {
    const middlePosition = window.innerHeight / 2; // middle
    window.scrollTo({ top: middlePosition + 100, behavior: "smooth" });
  }, [currentStep]);
  const navigate = useNavigate();
  return (
    <div className="sidebar-guideline-container">
      <SidebarGuideline
        isOpen={true}
        sidebarItems={[{ id: "Guideline", label: "Guideline" }]}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        selectedAwardCategory={formData.category}
      />
      <div className="application-form">
        <div className="form-header">
          <h1>{awardTitle}</h1>
          <p>Step {currentStep} of 5</p>
        </div>
        {error && <div className="error">{error}</div>}

        {isSubmitted ? (
          <div className="thank-you-message">
            <h2>Thank you for your submission!</h2>
            <p>Your Application has been successfully submitted.</p>
            <button onClick={() => navigate("/#Award%20Categories")}>
              Submit Another Application
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {renderStepContent()}
            {currentStep === 1 && (
              <div className="form-navigation-step1">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
            {currentStep > 1 && (
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-outline"
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                {currentStep < 5 && (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn btn-primary"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                )}
                {currentStep === 5 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={isSubmited()}
                    onClick={(e) => {
                      if (!formData.declaration) {
                        return alert(
                          "Please accept the declaration before submitting."
                        );
                      }
                      if (!formData.approvingAuthoritySignature) {
                        return alert(
                          "Please upload the approving authority signature file."
                        );
                      }
                      if (!window.confirm("Are you sure you want to submit?")) {
                        e.preventDefault();
                      }
                      alert("GNZ registration submitted successfully!");
                      setIsSubmitted(true);

                      handleSubmit(e, "submit");
                    }}
                  >
                    Submit
                  </button>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default RegistrationGNZ;
